import { arrayMove } from '@dnd-kit/sortable'
import { sortBy, uniq, xor } from 'lodash'

import { moveOnPinToggle, resolveColumnOrder } from './utils'

export const DEFAULT_COLUMNS_CONFIG = Object.freeze({ hidden: [], pinned: null, order: [] })

export function isColumnConfigurable({ enableColumnConfig = true }) {
  return enableColumnConfig
}

export function isAlwaysPinned({ alwaysPin = false }) {
  return alwaysPin
}

export function canReorder({ allowReorder = true }) {
  return allowReorder
}

export function alwaysPinnedKeys(items) {
  return items.filter(isAlwaysPinned).map(({ dataField, key }) => dataField ?? key)
}

export function resolvePinned(config, alwaysPinned = []) {
  return uniq([...alwaysPinned, ...config.pinned ?? []])
}

export function applyColumnsConfig(columns, config = DEFAULT_COLUMNS_CONFIG) {
  if (!columns?.length) {
    return columns
  }

  const pinned = resolvePinned(config, alwaysPinnedKeys(columns))
  const visible = columns.filter(column => isAlwaysPinned(column) || !isColumnConfigurable(column) || !config.hidden.includes(column.dataField))

  return sortBy(visible, [
    column => canReorder(column),
    column => isColumnConfigurable(column),
    column => !pinned.includes(column.dataField),
    column => {
      const index = config.order.indexOf(column.dataField)
      return index === -1 ? Infinity : index
    },
  ]).map(column => ({ ...column, pinned: pinned.includes(column.dataField) }))
}

export function togglePinColumn(config, catalog, key) {
  const alwaysPinned = alwaysPinnedKeys(catalog)
  if (alwaysPinned.includes(key)) {
    return config
  }

  const pinned = xor(resolvePinned(config, alwaysPinned), [key])
  const item = catalog.find(current => (current.key ?? current.dataField) === key)
  const keys = catalog.filter(canReorder).map(current => current.key ?? current.dataField)
  const order = resolveColumnOrder(config.order, keys)

  return {
    ...config,
    pinned,
    order: item && canReorder(item) ? moveOnPinToggle(order, key, pinned) : order,
  }
}

export function hideColumn(config, key) {
  return { ...config, hidden: uniq([...config.hidden, key]) }
}

export function reorderColumnsConfig(config, catalog, activeKey, overKey) {
  const byKey = new Map(catalog.map(item => [item.key, item]))
  const active = byKey.get(activeKey)
  const over = byKey.get(overKey)
  if (!active || !over || !canReorder(active) || !canReorder(over)) {
    return null
  }

  const pinned = resolvePinned(config, alwaysPinnedKeys(catalog))
  if (pinned.includes(activeKey) !== pinned.includes(overKey)) {
    return null
  }

  const keys = catalog.filter(canReorder).map(({ key }) => key)
  const order = resolveColumnOrder(config.order, keys)
  return { ...config, order: arrayMove(order, order.indexOf(activeKey), order.indexOf(overKey)) }
}
